import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import ProductOverview from './components/ProductOverview';
import FeaturesSpecs from './components/FeaturesSpecs';
import PurchaseSupport from './components/PurchaseSupport';
import ReviewsFAQ from './components/ReviewsFAQ';
import Footer from './components/Footer';
import Navbar from './components/Navbar';

export type Lang = 'en' | 'zh' | 'th' | 'vi' | 'ms';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? '';

const schemaWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Polyimide Film',
  url: SITE_URL,
  description: 'Premium polyimide film with thermal stability up to 400\u00b0C for aerospace, electronics, flexible PCB and new energy battery applications.',
  inLanguage: ['en', 'zh-CN', 'th', 'vi', 'ms'],
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const schemaOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Polyimide Film',
  url: SITE_URL,
  logo: 'https://public.readdy.ai/ai/img_res/2bd46c8d-4232-4ca7-9f70-77346d902090.png',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+65-8158-6399',
      contactType: 'sales',
      areaServed: ['SG', 'MY', 'TH', 'VN', 'CN'],
      availableLanguage: ['English', 'Chinese', 'Thai', 'Vietnamese', 'Malay'],
    },
  ],
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'SG',
    addressRegion: 'Singapore',
  },
  sameAs: [`https://wa.me/6581586399`],
};

const schemaProduct = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Polyimide Film — High Temperature Insulation Material',
  description: 'High-performance polyimide film offering exceptional thermal stability up to 400\u00b0C, superior electrical insulation, outstanding mechanical strength, and excellent chemical resistance. Ideal for aerospace, electronics, flexible PCB substrates, motor insulation, and new energy battery applications.',
  brand: {
    '@type': 'Brand',
    name: 'Polyimide Film',
  },
  manufacturer: {
    '@type': 'Organization',
    name: 'Polyimide Film',
    url: SITE_URL,
  },
  material: 'Polyimide',
  category: 'Industrial Insulation Materials',
  additionalProperty: [
    { '@type': 'PropertyValue', name: 'Temperature Range', value: '-269\u00b0C to +400\u00b0C' },
    { '@type': 'PropertyValue', name: 'Dielectric Strength', value: '\u226560 kV/mm' },
    { '@type': 'PropertyValue', name: 'Tensile Strength', value: '\u2265150 MPa' },
  ],
  offers: {
    '@type': 'Offer',
    availability: 'https://schema.org/InStock',
    priceCurrency: 'USD',
    seller: {
      '@type': 'Organization',
      name: 'Polyimide Film',
    },
    url: SITE_URL,
  },
};

const schemaFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the maximum operating temperature of polyimide film?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our polyimide film can withstand continuous operation from -269\u00b0C to +400\u00b0C, with short-term exposure up to 450\u00b0C.',
      },
    },
    {
      '@type': 'Question',
      name: 'What industries use polyimide film?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Polyimide film is widely used in aerospace, electronics, flexible PCB manufacturing, motor insulation, new energy battery applications, and semiconductor processing.',
      },
    },
    {
      '@type': 'Question',
      name: 'What thicknesses are available?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We offer polyimide film in a wide range of thicknesses from 12.5\u03bcm to 125\u03bcm, with custom thicknesses available upon request.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is the product ISO certified?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes, our polyimide film is manufactured under ISO 9001 quality management standards to ensure consistent high performance and reliability.',
      },
    },
  ],
};

function injectJsonLd(id: string, data: object): HTMLScriptElement {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return script;
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [lang, setLang] = useState<Lang>('en');

  useEffect(() => {
    const s1 = injectJsonLd('schema-website', schemaWebSite);
    const s2 = injectJsonLd('schema-organization', schemaOrganization);
    const s3 = injectJsonLd('schema-product', schemaProduct);
    const s4 = injectJsonLd('schema-faq', schemaFAQ);
    return () => {
      s1.remove();
      s2.remove();
      s3.remove();
      s4.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} lang={lang} onSetLang={setLang} />
      <Hero lang={lang} />
      <ProductOverview lang={lang} />
      <FeaturesSpecs lang={lang} />
      <PurchaseSupport lang={lang} />
      <ReviewsFAQ lang={lang} />
      <Footer lang={lang} />
    </div>
  );
}
